import {
  CharacterCount,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Textarea,
  useErrorToast,
} from '@h2hspark/ui';
import { Check, UserPlus } from 'lucide-react';
import * as React from 'react';

import { useSendConnectionRequest } from './api';
import {
  COMPOSER_CANCEL_LABEL,
  COMPOSER_CONSEQUENCE,
  COMPOSER_FAILED,
  COMPOSER_MESSAGE_LABEL,
  COMPOSER_SENDING_LABEL,
  COMPOSER_SENT_LABEL,
  COMPOSER_SEND_LABEL,
  MESSAGE_MAX_LENGTH,
  composerTitle,
  roleAtCompany,
} from './constants';

/**
 * The connection request composer (`MEM-032` AC-3, AC-4).
 *
 * ## Transcribed from `Member App.dc.html`, the connection request composer overlay
 *
 * AC-3 fixes the three things on it and the prototype draws them in this order: **who the request
 * is going to** (avatar, *"Connect with {name}"*, their role), **an editable message** under an
 * uppercase *Add a message* label, and **the consequence of sending** as a muted line beneath.
 * The send button is a full-width `--spark-gradient` pill.
 *
 * A `Dialog` rather than a hand-built full-screen overlay: `packages/ui` already has the
 * primitive, it carries the focus trap and the escape handling this needs
 * (NFR accessibility#wcag-aa), and `building-from-designs.md` says to reach for the component
 * before writing the markup. On a phone it fills the screen, which is the prototype's overlay.
 *
 * ## The consequence line, and the two rewrites it has had
 *
 * The prototype's helper reads *"They'll see your profile and this note. **You can message freely
 * once they accept.**"* Under ADR 032 there was no messaging, so the second sentence described a
 * feature that did not exist and the line was rewritten to promise the social channels instead.
 * ADR 052 supersedes ADR 032, `MEM-054` builds the thread, and `MEM-034` AC-6 makes **this very
 * note** its first message — so on 2026-09-12 the substitute became the false one, and the
 * prototype's promise is restored in substance.
 *
 * `COMPOSER_CONSEQUENCE` states the consequence that is true: they see the profile and the note,
 * accepting opens the pair's thread, the channels each chose to publish come with it
 * (`MEM-033` AC-5), and **neither of you is told if they do not** — which is `MEM-032` AC-7 said
 * to the person it protects, at the one moment they are deciding whether to send. AC-3 asks for
 * *the consequence of sending*, so a false one is not a presentation detail, which is why this
 * line has been rewritten twice rather than left to drift.
 *
 * ## Pre-filled from a match, and only from a match (AC-4)
 *
 * `prefilledMessage` comes from the surface that opened this. `match-card.tsx` passes
 * `prefilledMatchMessage(...)`; an attendee profile passes nothing, because an attendee profile
 * is not a match and pre-filling *"we matched"* there would be a claim the platform cannot
 * support. Editable either way — it is the member's message, not the platform's.
 *
 * ## After sending
 *
 * The control reports **Request sent** and the dialog closes. That state is not persisted, and
 * cannot be: there is no outbound view of a request (see the API's `repository.ts`), so after a
 * reload the Connect control returns to its default. Re-sending is idempotent, so nothing breaks
 * — and a persisted *Request sent* would have to be careful not to become the answer signal
 * `MEM-032` AC-7 removes.
 */
export function ConnectRequestDialog({
  open,
  person,
  eventId,
  attendeeRef,
  prefilledMessage,
  onOpenChange,
  onSent,
}: {
  readonly open: boolean;
  readonly person: {
    readonly firstName: string;
    readonly role: string | null;
    readonly company: string | null;
  };
  readonly eventId: string;
  readonly attendeeRef: string;
  /** `MEM-032` AC-4. `null` from any surface that is not a match. */
  readonly prefilledMessage: string | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSent: () => void;
}): React.ReactElement {
  const send = useSendConnectionRequest();
  const showError = useErrorToast();
  const [message, setMessage] = React.useState(prefilledMessage ?? '');

  React.useEffect(() => {
    if (open) setMessage(prefilledMessage ?? '');
  }, [open, prefilledMessage]);

  React.useEffect(() => {
    if (send.error !== null) showError(send.error, COMPOSER_FAILED);
  }, [send.error, showError]);

  const line = roleAtCompany(person);
  const tooLong = message.length > MESSAGE_MAX_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {/* AC-3, first: who the request is going to. */}
        <DialogHeader>
          <DialogTitle>{composerTitle(person.firstName)}</DialogTitle>
          {line === null ? null : <DialogDescription>{line}</DialogDescription>}
        </DialogHeader>

        {/* AC-3, second: an editable message. AC-4 pre-fills it from a match. */}
        <div className="space-y-2">
          <label
            htmlFor="connect-message"
            className="block font-brand text-2xs font-semibold uppercase tracking-caps text-text-subtle"
          >
            {COMPOSER_MESSAGE_LABEL}
          </label>
          <Textarea
            id="connect-message"
            value={message}
            className="min-h-[130px] resize-none"
            onChange={(event) => {
              setMessage(event.target.value);
            }}
          />
          <div className="flex justify-end">
            <CharacterCount used={message.length} max={MESSAGE_MAX_LENGTH} />
          </div>
        </div>

        {/* AC-3, third: the consequence of sending. See the header on why it is not the
            prototype's sentence. */}
        <p className="text-xs leading-snug text-text-subtle">{COMPOSER_CONSEQUENCE}</p>

        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            disabled={send.isPending || tooLong || send.isSuccess}
            onClick={() => {
              send.mutate(
                { eventId, attendeeRef, message },
                {
                  onSuccess: () => {
                    onSent();
                    onOpenChange(false);
                  },
                },
              );
            }}
            className="inline-flex w-full items-center justify-center gap-2 rounded-pill bg-spark p-3.5 font-brand text-md font-bold text-white shadow-accent disabled:opacity-60"
          >
            {send.isSuccess ? (
              <Check className="h-4.5 w-4.5" aria-hidden />
            ) : (
              <UserPlus className="h-4.5 w-4.5" aria-hidden />
            )}
            {send.isSuccess
              ? COMPOSER_SENT_LABEL
              : send.isPending
                ? COMPOSER_SENDING_LABEL
                : COMPOSER_SEND_LABEL}
          </button>
          <button
            type="button"
            disabled={send.isPending}
            onClick={() => {
              onOpenChange(false);
            }}
            className="inline-flex w-full items-center justify-center rounded-pill border-[1.5px] border-border bg-card p-3 font-brand text-sm font-bold text-text-body disabled:opacity-60"
          >
            {COMPOSER_CANCEL_LABEL}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
